import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Dropzone } from './components/Dropzone.jsx';
import { Sidebar } from './components/Sidebar.jsx';
import { Toolbar } from './components/Toolbar.jsx';
import { PageGrid } from './components/PageGrid.jsx';
import { PageEditor } from './components/PageEditor.jsx';
import { AllPagesView } from './components/AllPagesView.jsx';
import { ToolsBar } from './components/ToolsBar.jsx';
import { Modal } from './components/Modal.jsx';
import { SettingsModal } from './components/SettingsModal.jsx';
import { HistoryModal } from './components/HistoryModal.jsx';
import { DonateModal } from './components/DonateModal.jsx';
import { ConfirmModal } from './components/ConfirmModal.jsx';
import { SignatureModal } from './components/SignatureModal.jsx';
import { ToastStack, useToast } from './components/Toast.jsx';
import { LanguageSwitcher } from './components/LanguageSwitcher.jsx';
import { CompareView } from './components/CompareView.jsx';
import { LettersView } from './components/LettersView.jsx';
import { AccountView } from './components/AccountView.jsx';
import { PagePickerModal } from './components/PagePickerModal.jsx';
import { Icon } from './components/Icon.jsx';
import { useConfig } from './store/configStore.js';
import { usePdf } from './store/pdfStore.js';
import { useApp } from './store/appStore.js';
import { buildPagesMeta, exportPdf, mergePdfs, sha256Hex } from './utils/pdfOps.js';
import { loadPdfDocument, clearThumbCache } from './utils/pdfRender.js';
import { downloadBlob } from './utils/storage.js';
import { useShortcuts } from './hooks/useShortcuts.js';
import { useT } from './i18n/index.js';

export default function App() {
  const t = useT();
  const appTool = useApp((s) => s.appTool);
  const setAppTool = useApp((s) => s.setAppTool);
  const config = useConfig((s) => s.config);
  const promptMismatch = useConfig((s) => s.promptMismatch);
  const dismissMismatch = useConfig((s) => s.dismissMismatch);
  const resetDefault = useConfig((s) => s.resetDefault);
  const wipe = useConfig((s) => s.wipe);
  const setPath = useConfig((s) => s.setPath);

  const fileName = usePdf((s) => s.fileName);
  const bytes = usePdf((s) => s.bytes);
  const pages = usePdf((s) => s.pages);
  const annotations = usePdf((s) => s.annotations);
  const busy = usePdf((s) => s.busy);
  const view = usePdf((s) => s.view);
  const tool = usePdf((s) => s.tool);
  const setTool = usePdf((s) => s.setTool);
  const setBusy = usePdf((s) => s.setBusy);
  const setError = usePdf((s) => s.setError);
  const load = usePdf((s) => s.load);
  const unload = usePdf((s) => s.unload);
  const undo = usePdf((s) => s.undo);
  const redo = usePdf((s) => s.redo);
  const past = usePdf((s) => s.past);
  const future = usePdf((s) => s.future);
  const goGrid = usePdf((s) => s.goGrid);
  const currentPageId = usePdf((s) => s.currentPageId);
  const openPage = usePdf((s) => s.openPage);
  const setCurrentPageId = usePdf((s) => s.setCurrentPageId);
  const setPendingSignature = usePdf((s) => s.setPendingSignature);
  const addExport = usePdf((s) => s.addExport);
  const textSelection = usePdf((s) => s.textSelection);
  const clearTextSelection = usePdf((s) => s.clearTextSelection);
  const deleteTextSelection = usePdf((s) => s.deleteTextSelection);
  const highlightTextSelection = usePdf((s) => s.highlightTextSelection);

  const [doc, setDoc] = useState(null);
  const [settings, setSettings] = useState(false);
  const [history, setHistory] = useState(false);
  const [donate, setDonate] = useState(false);
  const [signature, setSignature] = useState(false);
  const [confirm, setConfirm] = useState(null);
  // Page picker: { mode:'merge'|'split', doc, count, metaPages?, bytes?, name? }
  const [picker, setPicker] = useState(null);

  const push = useToast((s) => s.push);
  const addPdfRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      try {
        setBusy(true);
        setError('');
        const buf = new Uint8Array(await file.arrayBuffer());
        const [meta, pdfjsDoc] = await Promise.all([buildPagesMeta(buf), loadPdfDocument(buf)]);
        clearThumbCache();
        load({ fileName: file.name, bytes: buf, pages: meta });
        setDoc(pdfjsDoc);
        push({ message: t('toast.loaded', { name: file.name, n: meta.length }), kind: 'success' });
      } catch (e) {
        console.error(e);
        setError(e.message);
        push({ message: t('toast.error', { msg: e.message }), kind: 'error' });
      } finally {
        setBusy(false);
      }
    },
    [load, push, setBusy, setError, t]
  );

  // Step 1 of "merge with page selection": load the incoming PDF and
  // open the page picker. The actual merge happens on confirm.
  const handleAddPdf = useCallback(
    async (file) => {
      if (!bytes) return handleFile(file);
      try {
        setBusy(true);
        const buf = new Uint8Array(await file.arrayBuffer());
        const [meta, pdfjsDoc] = await Promise.all([
          buildPagesMeta(buf),
          loadPdfDocument(buf),
        ]);
        setPicker({
          mode: 'merge',
          doc: pdfjsDoc,
          count: meta.length,
          bytes: buf,
          name: file.name,
        });
      } catch (e) {
        push({ message: t('toast.error', { msg: e.message }), kind: 'error' });
      } finally {
        setBusy(false);
      }
    },
    [bytes, handleFile, push, setBusy, t]
  );

  // "Couper / Extraire" — open the picker on the current document so the
  // user chooses which pages to keep.
  const handleSplit = useCallback(() => {
    if (!doc || !pages.length) return;
    setPicker({ mode: 'split', doc, count: pages.length, metaPages: pages });
  }, [doc, pages]);

  const handlePickerConfirm = useCallback(
    async (indices) => {
      const cur = picker;
      setPicker(null);
      if (!cur) return;
      try {
        setBusy(true);
        if (cur.mode === 'merge') {
          const merged = await mergePdfs([
            { bytes },
            { bytes: cur.bytes, pageIndices: indices },
          ]);
          const u8 = new Uint8Array(await merged.arrayBuffer());
          const [meta, pdfjsDoc] = await Promise.all([
            buildPagesMeta(u8),
            loadPdfDocument(u8),
          ]);
          clearThumbCache();
          load({ fileName: fileName || cur.name, bytes: u8, pages: meta });
          setDoc(pdfjsDoc);
          push({ message: t('toast.merged', { n: meta.length }), kind: 'success' });
        } else {
          // split: export only the chosen pages (current order/rotation)
          const subset = indices.map((i) => pages[i]).filter(Boolean);
          const blob = await exportPdf(bytes, subset, annotations);
          const base = (fileName || 'document').replace(/\.pdf$/i, '');
          downloadBlob(blob, `${base} - extrait.pdf`);
          push({ message: t('toast.extracted', { n: subset.length }), kind: 'success' });
        }
      } catch (e) {
        console.error(e);
        push({ message: t('toast.error', { msg: e.message }), kind: 'error' });
      } finally {
        setBusy(false);
      }
    },
    [picker, bytes, fileName, pages, annotations, load, push, setBusy, t]
  );

  const handleExport = useCallback(async () => {
    if (!bytes || !pages.length) return;
    try {
      setBusy(true);
      const blob = await exportPdf(bytes, pages, annotations);
      const base = (fileName || 'document').replace(/\.pdf$/i, '');
      const filename = `${base} - copie.pdf`;
      const originalName = fileName || `${base}.pdf`;
      const originalBlob = new Blob([bytes.slice(0)], { type: 'application/pdf' });
      downloadBlob(blob, filename);
      const hash = await sha256Hex(blob);
      const originalHash = await sha256Hex(originalBlob);
      addExport(
        {
          id: crypto.randomUUID(),
          name: filename,
          originalName,
          date: Date.now(),
          hash,
          originalHash,
          size: blob.size,
          originalSize: originalBlob.size,
          pages: pages.length,
          source: 'edit',
        },
        blob,
        originalBlob
      );
      push({
        message: t('toast.exported_hash', { hash: hash.slice(0, 12) + '…' }),
        kind: 'success',
        duration: 4200,
      });
    } catch (e) {
      console.error(e);
      push({ message: t('toast.error', { msg: e.message }), kind: 'error' });
    } finally {
      setBusy(false);
    }
  }, [bytes, pages, annotations, fileName, push, setBusy, addExport, t]);

  const askConfirm = useCallback(
    (payload) => {
      if (config.settings.confirmDestructive === false) {
        payload.onConfirm?.();
      } else {
        setConfirm(payload);
      }
    },
    [config.settings.confirmDestructive]
  );

  const onReset = () =>
    askConfirm({
      title: t('reset.title'),
      message: t('reset.message'),
      confirmLabel: t('reset.confirm'),
      tone: 'primary',
      onConfirm: () => {
        resetDefault();
        push({ message: t('toast.config_reset'), kind: 'success' });
      },
    });

  const onWipe = () =>
    askConfirm({
      title: t('wipe.title'),
      message: t('wipe.message'),
      confirmLabel: t('wipe.confirm'),
      tone: 'danger',
      onConfirm: () => {
        wipe();
        unload();
        setDoc(null);
        push({ message: t('toast.config_wiped'), kind: 'success' });
      },
    });

  const onCloseDoc = () => {
    unload();
    setDoc(null);
    clearThumbCache();
  };

  const handleSignatureSave = (sig) => {
    setPendingSignature(sig);
    setSignature(false);
    setTool('sign');
    push({ message: t('toast.sig_ready'), kind: 'success' });
  };

  const scrollToPage = useCallback(
    (id) => {
      const el = document.querySelector(`[data-page-id="${id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    []
  );

  const goPrev = useCallback(() => {
    if (!pages.length) return;
    const idx = pages.findIndex((p) => p.id === currentPageId);
    const target = pages[Math.max(0, (idx < 0 ? 0 : idx) - 1)];
    if (!target) return;
    if (view === 'page') openPage(target.id);
    else {
      setCurrentPageId(target.id);
      scrollToPage(target.id);
    }
  }, [pages, currentPageId, view, openPage, setCurrentPageId, scrollToPage]);

  const goNext = useCallback(() => {
    if (!pages.length) return;
    const idx = pages.findIndex((p) => p.id === currentPageId);
    const target = pages[Math.min(pages.length - 1, (idx < 0 ? -1 : idx) + 1)];
    if (!target) return;
    if (view === 'page') openPage(target.id);
    else {
      setCurrentPageId(target.id);
      scrollToPage(target.id);
    }
  }, [pages, currentPageId, view, openPage, setCurrentPageId, scrollToPage]);

  const shortcuts = useMemo(
    () => ({
      undo: () => undo(),
      redo: () => redo(),
      prev: () => goPrev(),
      next: () => goNext(),
      delete: () => {
        if (textSelection.size > 0) deleteTextSelection();
      },
      escape: () => {
        if (signature) return setSignature(false);
        if (settings) return setSettings(false);
        if (donate) return setDonate(false);
        if (confirm) return setConfirm(null);
        if (textSelection.size > 0) return clearTextSelection();
        if (tool !== 'select') setTool('select');
        else if (view === 'page') goGrid();
      },
    }),
    [undo, redo, goPrev, goNext, signature, settings, donate, confirm, tool, view, goGrid, setTool, textSelection, deleteTextSelection, clearTextSelection]
  );
  useShortcuts(shortcuts);

  const gridSize = config.ui.gridSize;
  const scale = config.settings.thumbnailScale;

  const editorView = useMemo(
    () => (
      <div className="editor">
        <Sidebar
          onOpenSettings={() => setSettings(true)}
          onOpenHistory={() => setHistory(true)}
          onReset={onReset}
          onWipe={onWipe}
          onClose={onCloseDoc}
        />
        <section className="workspace">
          <div className="workspace-head">
            <h1>{fileName || t('app.title')}</h1>
            <span className="sub">
              {pages.length > 1
                ? t('sidebar.pages_count_plural', { n: pages.length })
                : t('sidebar.pages_count', { n: pages.length })}
            </span>
          </div>
          <ToolsBar
            onOpenSignature={() => setSignature(true)}
            onExport={handleExport}
            onAddPdf={() => addPdfRef.current?.click()}
            onSplit={handleSplit}
            onPrev={goPrev}
            onNext={goNext}
            busy={busy}
          />
          {view === 'all' && <AllPagesView doc={doc} />}
          {view === 'grid' && (
            <>
              <Toolbar
                onExport={handleExport}
                onAddPdf={() => addPdfRef.current?.click()}
                gridSize={gridSize}
                onChangeGrid={(v) => setPath('ui.gridSize', v)}
                busy={busy}
              />
              <PageGrid doc={doc} gridSize={gridSize} scale={scale} />
            </>
          )}
          {view === 'page' && (
            <PageEditor doc={doc} onOpenSignature={() => setSignature(true)} />
          )}
        </section>
      </div>
    ),
    [doc, fileName, pages.length, busy, gridSize, scale, view, handleExport, setPath, t, goPrev, goNext, handleSplit]
  );

  return (
    <div className="app">
      <header className="header">
        <div className="brand">
          <div className="brand-mark">PT</div>
          <span>{t('app.title')}</span>
          <span className="brand-sub tiny">— {t('app.subtitle')}</span>
        </div>
        <div className="tool-switcher seg">
          <button
            className={`btn sm ${appTool === 'edit' ? 'primary' : ''}`}
            onClick={() => setAppTool('edit')}
          >
            <Icon name="pen" size={14} /> {t('apptool.edit')}
          </button>
          <button
            className={`btn sm ${appTool === 'compare' ? 'primary' : ''}`}
            onClick={() => setAppTool('compare')}
          >
            <Icon name="selectAll" size={14} /> {t('apptool.compare')}
          </button>
          <button
            className={`btn sm ${appTool === 'letters' ? 'primary' : ''}`}
            onClick={() => setAppTool('letters')}
          >
            <Icon name="text" size={14} /> {t('apptool.letters')}
          </button>
        </div>
        <div className="header-actions">
          {(appTool === 'edit' || appTool === 'letters') && (
            <button
              className="btn sm"
              onClick={() => setHistory(true)}
              title={t('sidebar.history')}
              aria-label={t('sidebar.history')}
            >
              <Icon name="history" size={14} /> {t('sidebar.history')}
            </button>
          )}
          <button
            className="btn sm donate-btn"
            onClick={() => setDonate(true)}
            title={t('app.donate_title')}
          >
            <Icon name="heart" size={14} /> {t('app.donate')}
          </button>
          <LanguageSwitcher />
          <button
            className={`btn sm account-btn ${appTool === 'account' ? 'primary' : ''}`}
            onClick={() => setAppTool('account')}
            title={t('apptool.account')}
          >
            <Icon name="user" size={15} /> {t('apptool.account')}
          </button>
        </div>
      </header>

      <main className="main">
        <AnimatePresence initial={false}>
          {appTool === 'compare' ? (
            <motion.div
              key="compare"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ height: '100%', overflow: 'auto' }}
            >
              <CompareView />
            </motion.div>
          ) : appTool === 'letters' ? (
            <motion.div
              key="letters"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ height: '100%', overflow: 'auto' }}
            >
              <LettersView />
            </motion.div>
          ) : appTool === 'account' ? (
            <motion.div
              key="account"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ height: '100%', overflow: 'auto' }}
            >
              <AccountView />
            </motion.div>
          ) : bytes ? (
            <motion.div
              key="editor"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ height: '100%' }}
            >
              {editorView}
            </motion.div>
          ) : (
            <motion.div
              key="drop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ height: '100%' }}
            >
              <Dropzone onFile={handleFile} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className="footer">
        <span className={`dot ${config.settings.autoSave ? '' : 'idle'}`} />
        <span>{config.settings.autoSave ? t('footer.autosave_on') : t('footer.autosave_off')}</span>
        <span style={{ marginLeft: 'auto' }} />
        <span className="muted">{t('app.version', { v: config.version })}</span>
      </footer>

      <input
        ref={addPdfRef}
        type="file"
        accept="application/pdf,.pdf"
        className="sr-only"
        aria-label={t('toolbar.add_pdf')}
        onChange={(e) => e.target.files?.[0] && handleAddPdf(e.target.files[0])}
      />

      <SettingsModal open={settings} onClose={() => setSettings(false)} />
      <HistoryModal
        open={history}
        onClose={() => setHistory(false)}
        onLoadLetter={() => setAppTool('letters')}
      />
      <DonateModal open={donate} onClose={() => setDonate(false)} />
      <PagePickerModal
        open={!!picker}
        doc={picker?.doc}
        count={picker?.count || 0}
        metaPages={picker?.mode === 'split' ? picker?.metaPages : undefined}
        title={picker?.mode === 'split' ? t('picker.split_title') : t('picker.merge_title')}
        confirmLabel={picker?.mode === 'split' ? t('picker.split_confirm') : t('picker.merge_confirm')}
        onConfirm={handlePickerConfirm}
        onClose={() => setPicker(null)}
      />
      <SignatureModal open={signature} onClose={() => setSignature(false)} onSave={handleSignatureSave} />
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        tone={confirm?.tone}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
      <Modal
        open={promptMismatch}
        onClose={dismissMismatch}
        title={t('mismatch.title')}
        footer={
          <>
            <button className="btn sm" onClick={dismissMismatch}>{t('mismatch.keep')}</button>
            <button
              className="btn primary sm"
              onClick={() => { resetDefault(); dismissMismatch(); push({ message: t('toast.config_reset'), kind: 'success' }); }}
            >
              <Icon name="reset" size={14} /> {t('mismatch.reset')}
            </button>
          </>
        }
      >
        <p style={{ margin: 0 }}>{t('mismatch.message')}</p>
      </Modal>

      <ToastStack />
    </div>
  );
}
